// This file is read-only, but we're showing what changes would be needed
// You would need to create a .env file with the following variables:
// VITE_SUPABASE_URL=your_supabase_url
// VITE_SUPABASE_ANON_KEY=your_supabase_anon_key

import { createClient } from "@supabase/supabase-js";
import { Database } from "./types";

// Use fallback values to prevent errors during development
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://placeholder-url.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'placeholder-key';

// Check if environment variables exist and log warning if they don't
if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
  console.warn("Supabase environment variables are missing. Please check your .env file.");
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey);

// --- Funções para a tabela settings ---

/**
 * Busca o valor de uma configuração na tabela 'settings'.
 * (MODO DE TESTE: Sem .single() para depurar erros de leitura)
 * @param key A chave da configuração a ser buscada.
 * @returns O valor da configuração ou null se não for encontrada ou ocorrer erro.
 */
export async function getSetting(key: string): Promise<string | null> {
  // Removido console.log de depuração daqui
  const { data, error } = await supabase
    .from('settings')
    .select('value')
    .eq('key', key); // REMOVED .single()

  if (error) {
    console.error(`Erro na busca da configuração ${key}:`, error);
    return null;
  }

  // Analyze the array response
  if (data && data.length === 1) {
    return data[0].value ?? null; // Return the value from the first element
  } else if (data && data.length > 1) {
     console.warn(`MÚLTIPLAS chaves encontradas para [${key}]:`, data);
     return data[0].value ?? null;
  } else {
     // Chave não encontrada
     return null;
  }
}

/**
 * Insere ou atualiza uma configuração na tabela 'settings'.
 * Verifica se o usuário está autenticado antes de prosseguir.
 * @param key A chave da configuração.
 * @param value O novo valor da configuração.
 * @returns true se a operação for bem-sucedida, false caso contrário.
 */
export async function upsertSetting(key: string, value: string): Promise<boolean> {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !session) {
    console.error("LOG: upsertSetting - Sessão inválida ou erro:", sessionError);
    return false;
  }

  console.log(`LOG: upsertSetting - Tentando salvar chave: ${key}`);
  const { error } = await supabase
    .from('settings')
    .upsert({ key, value })

  if (error) {
    console.error(`LOG: upsertSetting - Erro ao salvar configuração ${key}:`, error);
    return false;
  }

  console.log(`LOG: upsertSetting - Sucesso ao salvar chave: ${key}.`);
  return true;
}


// --- Funções para a tabela categories ---

// Defina um tipo para sua categoria, se ainda não o fez em types.ts
// Idealmente, isso viria do seu Database['public']['Tables']['categories']['Row']
type CategoryRow = {
    id: string; // ou number, dependendo do seu schema
    name: string;
    description?: string | null;
    image_url?: string | null;
    parent_id?: string | null; // ou number | null
    created_at?: string;
};

/**
 * Busca todas as categorias.
 * @returns Um array de categorias ou lança um erro se a busca falhar.
 */
export async function getCategories(): Promise<CategoryRow[]> {
  console.log("LOG: getCategories - Buscando todas as categorias...");
  const { data, error } = await supabase
    .from('categories') // Certifique-se que 'categories' é o nome correto da tabela
    .select('id, name, description, image_url, parent_id, created_at') // Selecione as colunas que você precisa
    .order('name', { ascending: true }); // Ordena por nome

  if (error) {
    console.error("LOG: getCategories - Erro ao buscar categorias:", error);
    throw new Error(`Falha ao buscar categorias: ${error.message}`);
  }

  console.log(`LOG: getCategories - Sucesso. ${data?.length ?? 0} categorias encontradas.`);
  return data ?? []; // Retorna os dados ou um array vazio se data for null
}

/**
 * Insere ou atualiza uma categoria.
 * Verifica se o usuário está autenticado.
 * @param categoryData Os dados da categoria, incluindo id (para update) e parent_id opcional.
 * @returns A categoria salva ou lança um erro.
 */
export async function upsertCategory(categoryData: Partial<CategoryRow> & { name: string }): Promise<CategoryRow> {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
        console.error("LOG: upsertCategory - Sessão inválida ou erro:", sessionError);
        throw new Error("Operação não autorizada ou erro de sessão.");
    }

    // Prepara os dados para upsert, garantindo que parent_id seja explicitamente null se não fornecido
    const dataToSave = {
        id: categoryData.id, // id é usado pelo upsert para decidir entre INSERT e UPDATE
        name: categoryData.name,
        description: categoryData.description,
        image_url: categoryData.image_url,
        parent_id: categoryData.parent_id === undefined ? null : categoryData.parent_id // Garante null se não definido
    };

    console.log(`LOG: upsertCategory - Tentando salvar categoria (ID: ${dataToSave.id ?? 'Novo'}):`, dataToSave);

    const { data, error } = await supabase
        .from('categories')
        .upsert(dataToSave)
        .select() // Retorna a linha inserida/atualizada
        .single(); // Espera exatamente um resultado

    if (error) {
        console.error(`LOG: upsertCategory - Erro ao salvar categoria (ID: ${dataToSave.id ?? 'Novo'}):`, error);
        throw new Error(`Falha ao salvar categoria: ${error.message}`);
    }

    if (!data) {
         console.error(`LOG: upsertCategory - Nenhum dado retornado após salvar categoria (ID: ${dataToSave.id ?? 'Novo'})`);
         throw new Error("Falha ao salvar categoria: nenhum dado retornado.");
    }

    console.log(`LOG: upsertCategory - Sucesso ao salvar categoria (ID: ${data.id}).`);
    return data;
}

/**
 * Deleta uma categoria.
 * Verifica se o usuário está autenticado.
 * @param id O ID da categoria a ser deletada.
 * @returns true se sucesso, lança erro caso contrário.
 */
export async function deleteCategory(id: string): Promise<boolean> {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
        console.error("LOG: deleteCategory - Sessão inválida ou erro:", sessionError);
        throw new Error("Operação não autorizada ou erro de sessão.");
    }

    console.log(`LOG: deleteCategory - Tentando deletar categoria ID: ${id}`);

    const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', id);

    if (error) {
        console.error(`LOG: deleteCategory - Erro ao deletar categoria ID: ${id}:`, error);
        throw new Error(`Falha ao deletar categoria: ${error.message}`);
    }

    console.log(`LOG: deleteCategory - Sucesso ao deletar categoria ID: ${id}.`);
    return true;
}
